import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type PropsWithChildren,
} from "react";
import { hideFrontChat } from "util/openFrontChat";

type DismissScoutHandler = () => void;

/** Information supplied by an in-page "Ask Scout" trigger. */
export type ScoutQuestionRequest = {
  question: string;
  context?: string;
};

/**
 * Identified one-shot command delivered to the popover owner. Request identity
 * is plumbing state: callers can ask the same question more than once without
 * manufacturing a nonce themselves.
 */
export type ScoutQuestionCommand = ScoutQuestionRequest & {
  requestId: number;
};

type OpenScoutWithQuestionHandler = (command: ScoutQuestionCommand) => void;

type ChatSurfaceCoordinatorContextValue = {
  /**
   * Register a handler that closes this Scout surface when another chat opens.
   * Returns an unsubscribe function — return it from a `useEffect` cleanup so
   * the handler is removed on unmount.
   */
  registerDismissScout: (handler: DismissScoutHandler) => () => void;
  /** Registered by the surface that owns the Scout popover. */
  registerOpenScoutWithQuestion: (handler: OpenScoutWithQuestionHandler) => () => void;
  /** No-op where no Scout surface is mounted (e.g. staff Observe). */
  openScoutWithQuestion: (request: ScoutQuestionRequest) => void;
  /** Call before opening a Scout popover so Front support chat closes. */
  notifyScoutPopoverOpening: () => void;
  /** Call when Front's messenger panel becomes visible. */
  notifyFrontChatOpened: () => void;
};

const ChatSurfaceCoordinatorContext = createContext<ChatSurfaceCoordinatorContextValue | null>(
  null
);

/**
 * Ensures at most one bottom-left chat surface is open: Scout popover or Front
 * support chat. Scout triggers hide Front on open; Front visibility dismisses
 * Scout popovers/minimized bars without resetting conversation session.
 */
export function ChatSurfaceCoordinatorProvider({ children }: PropsWithChildren) {
  const dismissScoutHandlersRef = useRef(new Set<DismissScoutHandler>());
  const openScoutWithQuestionRef = useRef<OpenScoutWithQuestionHandler | undefined>(undefined);
  const nextScoutQuestionRequestIdRef = useRef(0);

  const registerDismissScout = useCallback((handler: DismissScoutHandler) => {
    dismissScoutHandlersRef.current.add(handler);
    return () => {
      dismissScoutHandlersRef.current.delete(handler);
    };
  }, []);

  // Single handler rather than a set: exactly one surface owns the popover.
  const registerOpenScoutWithQuestion = useCallback((handler: OpenScoutWithQuestionHandler) => {
    openScoutWithQuestionRef.current = handler;
    return () => {
      if (openScoutWithQuestionRef.current === handler) {
        openScoutWithQuestionRef.current = undefined;
      }
    };
  }, []);

  const openScoutWithQuestion = useCallback((request: ScoutQuestionRequest) => {
    const handler = openScoutWithQuestionRef.current;
    if (!handler) {
      return;
    }
    nextScoutQuestionRequestIdRef.current += 1;
    handler({ ...request, requestId: nextScoutQuestionRequestIdRef.current });
  }, []);

  const dismissAllScoutSurfaces = useCallback(() => {
    for (const handler of dismissScoutHandlersRef.current) {
      handler();
    }
  }, []);

  const notifyScoutPopoverOpening = useCallback(() => {
    hideFrontChat();
  }, []);

  const notifyFrontChatOpened = useCallback(() => {
    dismissAllScoutSurfaces();
  }, [dismissAllScoutSurfaces]);

  const value = useMemo(
    () => ({
      registerDismissScout,
      registerOpenScoutWithQuestion,
      openScoutWithQuestion,
      notifyScoutPopoverOpening,
      notifyFrontChatOpened,
    }),
    [
      registerDismissScout,
      registerOpenScoutWithQuestion,
      openScoutWithQuestion,
      notifyScoutPopoverOpening,
      notifyFrontChatOpened,
    ]
  );

  return (
    <ChatSurfaceCoordinatorContext.Provider value={value}>
      {children}
    </ChatSurfaceCoordinatorContext.Provider>
  );
}

export function useChatSurfaceCoordinator(): ChatSurfaceCoordinatorContextValue | null {
  return useContext(ChatSurfaceCoordinatorContext);
}
